import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { authAPI } from '../services/api';
import PageTransition from '../components/animations/PageTransition';
import { useLanguage } from '../contexts/LanguageContext';

function Signup() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_no: '',
    password: '',
    confirmPassword: '',
    role: 'student',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // 1. Create Firebase User
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      // 2. Update Profile (Display Name)
      await updateProfile(user, {
        displayName: formData.name
      });

      // 3. Sync with backend
      let role = formData.role || 'student';
      let userId = user.uid;
      let token = '';

      try {
        const idToken = await user.getIdToken();
        token = idToken;
        const syncRes = await authAPI.syncUser({ 
          firebase_token: idToken,
          mobile_no: formData.mobile_no 
        });
        if (syncRes && syncRes.data) {
          token = syncRes.data.access_token || idToken;
          role = syncRes.data.role || role;
          userId = syncRes.data.user_id || userId;
        }
      } catch (syncErr) {
        console.warn('Backend syncUser unavailable, continuing with Firebase auth session:', syncErr);
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({
        uid: userId,
        email: user.email,
        name: formData.name,
        role: role
      }));

      // Navigate based on role
      if (role === 'instructor') {
        navigate('/instructor-dashboard');
      } else {
        navigate('/');
      }

    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Email is already registered.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak.');
      } else {
        setError('Registration failed: ' + err.message);
      }
    }

    setLoading(false);
  };

  const handleGoogleSignup = async () => {
    setError('');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      let role = 'student';
      let userId = user.uid;
      let token = '';

      const adminEmails = [
        'dr.sabitha@arivunest.com',
        'nandhakumar@arivunest.com',
        'kanirathinamfoundation@gmail.com',
        'smnk2006@gmail.com'
      ];
      if (adminEmails.includes((user.email || '').toLowerCase())) {
        role = 'admin';
      }

      try {
        const idToken = await user.getIdToken();
        token = idToken;
        const syncRes = await authAPI.syncUser({ firebase_token: idToken });
        if (syncRes && syncRes.data) {
          token = syncRes.data.access_token || idToken;
          role = syncRes.data.role || role;
          userId = syncRes.data.user_id || userId;
        }
      } catch (syncErr) {
        console.warn('Backend syncUser unavailable, continuing with Firebase auth session:', syncErr);
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({
        uid: userId,
        email: user.email,
        name: user.displayName || 'User',
        role: role
      }));

      // Redirect
      if (role === 'instructor') {
        navigate('/instructor-dashboard');
      } else if (role === 'admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/');
      }

    } catch (err) {
      console.error(err);
      setError('Google Sign-In failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <PageTransition>
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.overlay} />

          {/* Animated Background Elements */}
          {!isMobile && <div style={styles.orb1} />}
          {!isMobile && <div style={styles.orb2} />}

          <div style={{...styles.glassCard, flexDirection: isMobile ? 'column' : 'row'}}>
            {/* Left Panel - Branding */}
            {!isMobile && <div style={styles.leftPanel}>
              <div style={styles.brandContainer}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                  <img src="/images/logos/arivu-nest-logo.png" alt="Arivu Nest" style={{ height: '48px', width: 'auto' }} />
                  <div>
                    <h1 style={{ ...styles.brandTitle, margin: 0, fontSize: '30px' }}>ARIVU NEST</h1>
                    <div style={{ fontSize: '11px', color: '#a7f3d0', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase' }}>
                      Research & Development
                    </div>
                  </div>
                </div>
                <p style={styles.brandSubtitle}>{t('masterEnglishWithConfidence')}</p>
              </div>
              <div style={styles.featureList}>
                <div style={styles.featureItem}>
                  <span style={styles.featureIcon}>🎤</span>
                  <span>{t('realTimePronunciation')}</span>
                </div>
                <div style={styles.featureItem}>
                  <span style={styles.featureIcon}>📊</span>
                  <span>{t('detailedProgressAnalytics')}</span>
                </div>
                <div style={styles.featureItem}>
                  <span style={styles.featureIcon}>🏆</span>
                  <span>{t('gamifiedLearning')}</span>
                </div>
              </div>
            </div>}

            {/* Right Panel - Form */}
            <div style={styles.rightPanel}>
              <h2 style={styles.formTitle}>{t('createAccount')}</h2>
              <p style={styles.formSubtitle}>{t('joinThousandsOfLearners')}</p>

              {error && <div style={styles.error}>{error}</div>}

              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>{t('fullName')}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder={t('enterYourName')}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>{t('emailAddress')}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder={t('enterYourEmail')}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>{t('phoneNumber')}</label>
                  <input
                    type="tel"
                    name="mobile_no"
                    value={formData.mobile_no}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder={t('phoneNumber')}
                    required
                  />
                </div>

                <div style={{...styles.row, flexDirection: isMobile ? 'column' : 'row'}}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>{t('password')}</label>
                    <div style={styles.passwordWrapper}>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder={t('minChars')}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={styles.eyeIcon}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>{t('confirm')}</label>
                    <div style={styles.passwordWrapper}>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder={t('confirm')}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={styles.eyeIcon}
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>
                </div>

                <button type="submit" disabled={loading} style={styles.button}>
                  {loading ? t('creatingAccount') : t('signUp')}
                </button>
              </form>

              <div style={styles.divider}>
                <span>{t('or')}</span>
              </div>

              <button onClick={handleGoogleSignup} type="button" disabled={loading} style={styles.googleButton}>
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" style={styles.googleIcon} />
                {t('signUpWithGoogle')}
              </button>

              <div style={styles.footerLink}>
                {t('alreadyHaveAccount')} <span onClick={() => navigate('/login')} style={styles.linkText}>{t('login')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#0f172a' },
  container: {
    position: 'relative', width: '100vw', minHeight: '100vh', display: 'flex',
    justifyContent: 'center', alignItems: 'center',
    background: 'radial-gradient(circle at 10% 20%, rgb(33, 43, 89) 0%, rgb(15, 23, 42) 90.1%)',
    backgroundSize: 'cover', fontFamily: "'Segoe UI', sans-serif",
    padding: '20px', boxSizing: 'border-box',
  },
  overlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 1 },
  orb1: {
    position: 'absolute', top: '10%', left: '10%', width: '300px', height: '300px',
    background: 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, rgba(99,102,241,0) 70%)',
    borderRadius: '50%', zIndex: 0, filter: 'blur(40px)',
  },
  orb2: {
    position: 'absolute', bottom: '10%', right: '10%', width: '400px', height: '400px',
    background: 'radial-gradient(circle, rgba(168,85,247,0.3) 0%, rgba(168,85,247,0) 70%)',
    borderRadius: '50%', zIndex: 0, filter: 'blur(50px)',
  },
  glassCard: {
    position: 'relative', zIndex: 2, display: 'flex', width: '100%', maxWidth: '1000px',
    background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', overflow: 'hidden',
    height: 'auto', minHeight: '600px'
  },
  leftPanel: {
    flex: '1', padding: '60px 40px', background: 'rgba(0,0,0,0.2)',
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    color: 'white', borderRight: '1px solid rgba(255,255,255,0.1)',
  },
  brandTitle: { fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: '800', margin: '0 0 10px 0', background: 'linear-gradient(to right, #818cf8, #c084fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  brandSubtitle: { fontSize: '18px', color: '#94a3b8', margin: 0 },
  featureList: { marginTop: '60px', display: 'flex', flexDirection: 'column', gap: '30px' },
  featureItem: { display: 'flex', alignItems: 'center', gap: '20px', fontSize: '16px', color: '#e2e8f0' },
  featureIcon: { fontSize: '24px', background: 'rgba(255,255,255,0.1)', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' },

  rightPanel: { flex: '1.2', padding: 'clamp(24px, 5vw, 60px) clamp(16px, 4vw, 50px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'rgba(255,255,255,0.02)' },
  formTitle: { fontSize: '28px', fontWeight: '700', color: 'white', margin: '0 0 8px 0' },
  formSubtitle: { color: '#94a3b8', margin: '0 0 32px 0', fontSize: '15px' },
  error: { background: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', padding: '12px', borderRadius: '8px', marginBottom: '24px', border: '1px solid rgba(239, 68, 68, 0.3)', fontSize: '14px' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' },
  row: { display: 'flex', gap: '20px' },
  label: { fontSize: '14px', color: '#cbd5e1', fontWeight: '500' },
  input: {
    width: '100%', padding: '12px 16px', background: 'rgba(15, 23, 42, 0.6)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
    color: 'white', fontSize: '15px', outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  passwordWrapper: { position: 'relative', width: '100%' },
  eyeIcon: {
    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '16px'
  },
  selectWrapper: { position: 'relative', width: '100%' },
  select: {
    width: '100%', padding: '12px 16px', background: 'rgba(15, 23, 42, 0.6)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
    color: 'white', fontSize: '15px', outline: 'none', cursor: 'pointer'
  },
  button: {
    width: '100%', padding: '14px', background: 'linear-gradient(to right, #4f46e5, #7c3aed)',
    color: 'white', border: 'none', borderRadius: '12px',
    fontSize: '16px', fontWeight: '600', cursor: 'pointer',
    marginTop: '10px', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
    transition: 'transform 0.2s'
  },
  divider: { display: 'flex', alignItems: 'center', margin: '24px 0', color: '#64748b', fontSize: '14px', gap: '10px', before: { content: '""', flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }, after: { content: '""', flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' } },
  googleButton: {
    width: '100%', padding: '12px', background: 'white', color: '#1e293b',
    border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
    transition: 'background 0.2s'
  },
  googleIcon: { width: '20px', height: '20px' },
  footerLink: { marginTop: '30px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' },
  linkText: { color: '#818cf8', fontWeight: '600', cursor: 'pointer', marginLeft: '5px' }
};

export default Signup;
